import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Navbar from '../../components/Header';

const Blog = () => {
    const [newsArticles, setNewsArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [currentPage, setCurrentPage] = useState(1); // State for current "slide" or page
    const postsPerPage = 16; // Number of posts per "slide"

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/blog`);
                const reversedData = res.data.reverse();
                setNewsArticles(reversedData);
            } catch (err) {
                console.error("Error fetching blogs:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
        window.scrollTo(0, 0);
    }, []);

    const categories = useMemo(() => {
        return [...new Set(newsArticles.map(n => n.category))];
    }, [newsArticles]);

    const totalFilteredArticles = useMemo(() => {
        let filtered = newsArticles;

        if (selectedCategory) {
            filtered = filtered.filter(article => article.category === selectedCategory);
        }

        if (searchTerm.trim() !== '') {
            filtered = filtered.filter(article =>
                article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                article.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return filtered.length;
    }, [searchTerm, selectedCategory, newsArticles]);

    const totalPages = Math.ceil(totalFilteredArticles / postsPerPage);

    const paginatedArticles = useMemo(() => {
        let filtered = newsArticles;

        if (selectedCategory) {
            filtered = filtered.filter(article => article.category === selectedCategory);
        }

        if (searchTerm.trim() !== '') {
            filtered = filtered.filter(article =>
                article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                article.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        } else if (totalPages === 0) {
            setCurrentPage(1);
        }

        const indexOfLastPost = currentPage * postsPerPage;
        const indexOfFirstPost = indexOfLastPost - postsPerPage;
        return filtered.slice(indexOfFirstPost, indexOfLastPost);

    }, [searchTerm, selectedCategory, newsArticles, currentPage, postsPerPage, totalPages]); // Added totalPages to dependencies

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prevPage => prevPage + 1);
            window.scrollTo(0, 0); // Scroll to top when navigating
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prevPage => prevPage - 1);
            window.scrollTo(0, 0); // Scroll to top when navigating
        }
    };

    return (
        <>
            <Navbar diffHeader={true} />
            <div className="min-h-screen bg-white font-inter text-gray-900">
                <div className="h-24 sm:h-32"></div>

                <main className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
                    <section className="text-center mb-12">
                        <h1 className="text-4xl pt-6 sm:text-5xl font-extrabold text-gray-800 tracking-tight mb-4">
                            News, Media Gallery & Insights
                        </h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Take a look at the latest Real Estate News, Videos & Insights.
                        </p>
                    </section>

                    <section className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 px-2 sm:px-0">
                        {/* Search Input */}
                        <div className="relative w-full sm:w-auto flex-grow max-w-sm">
                            <input
                                type="text"
                                placeholder="Search by keyword"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1); // Reset to first page on search
                                }}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                            />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                                </svg>
                            </div>
                        </div>

                        {/* Category Dropdown */}
                        <div className="relative w-full sm:w-auto">
                            <select
                                value={selectedCategory}
                                onChange={(e) => {
                                    setSelectedCategory(e.target.value);
                                    setCurrentPage(1); // Reset to first page on category change
                                }}
                                className="block w-full appearance-none bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-lg shadow-sm leading-tight focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Categories</option>
                                {categories.map((cat, idx) => (
                                    <option key={idx} value={cat}>{cat}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </div>
                        </div>
                    </section>

                    {loading ? (
                        <p className="text-center text-gray-500">Loading...</p>
                    ) : (
                        <>
                            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-8">
                                {paginatedArticles.length === 0 ? (
                                    <p className="text-center col-span-full text-gray-500">No results found.</p>
                                ) : (
                                    paginatedArticles.map((article) => (
                                        <Link to={`/blog/${article.id}`} className="block">
                                            <div
                                                key={article.id}
                                                className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:scale-[1.02] transform h-full flex flex-col" // Added h-full and flex flex-col
                                            >
                                                <div className="relative h-48 sm:h-56 flex-shrink-0"> {/* Added flex-shrink-0 */}
                                                    <img
                                                        src={article.image}
                                                        alt={article.title}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = `https://placehold.co/400x250/CCCCCC/333333?text=Image`;
                                                        }}
                                                    />
                                                    <span className="absolute bottom-2 left-2 bg-[#173d73] text-white text-xs font-semibold px-3 py-1 rounded-full">
                                                        {article.category}
                                                    </span>
                                                </div>
                                                <div className="p-5 flex flex-col flex-grow"> {/* Added flex flex-col and flex-grow */}
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-3 leading-tight min-h-[50px] flex items-start overflow-hidden text-ellipsis line-clamp-2"> {/* Added overflow-hidden, text-ellipsis, line-clamp-2 */}
                                                        {article.title}
                                                    </h3>
                                                    <p className="text-gray-500 text-sm flex-grow overflow-hidden text-ellipsis line-clamp-3"> {/* Added flex-grow, overflow-hidden, text-ellipsis, line-clamp-3 */}
                                                        {article.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))

                                )}
                            </section>

                            {/* Pagination Controls */}
                            {totalFilteredArticles > postsPerPage && ( // Only show controls if there are more articles than fit on one page
                                <div className="flex justify-center items-center gap-4 py-8">
                                    <button
                                        onClick={handlePreviousPage}
                                        disabled={currentPage === 1}
                                        className="px-6 py-3 bg-[#173d73] text-white rounded-lg shadow-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-gray-700 font-medium">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        className="px-6 py-3 bg-[#173d73] text-white rounded-lg shadow-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
            <Footer />
        </>
    );
};

export default Blog;